import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Users, TrendingUp, Handshake, Target } from "lucide-react";
import PartnershipPipeline from "@/components/sales/partners/PartnershipPipeline";
import PartnerProspectForm from "@/components/sales/partners/PartnerProspectForm";
import ActivePartnerships from "@/components/sales/partners/ActivePartnerships";
import PartnershipCategories from "@/components/sales/partners/PartnershipCategories";
import PartnershipAnalytics from "@/components/sales/partners/PartnershipAnalytics";

const PartnersPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showProspectForm, setShowProspectForm] = useState(false);

  // Mock quick stats data
  const quickStats = [
    {
      title: "Active Partnerships",
      value: "24",
      description: "Currently active partnership agreements",
      icon: Handshake,
      trend: "+3 this month",
      color: "text-primary"
    },
    {
      title: "Revenue Sharing",
      value: "$127K",
      description: "Total monthly revenue from partnerships",
      icon: TrendingUp,
      trend: "+15% from last month",
      color: "text-success"
    },
    {
      title: "Pipeline Value",
      value: "$450K",
      description: "Potential value in partnership pipeline",
      icon: Target,
      trend: "8 prospects in negotiation",
      color: "text-warning"
    },
    {
      title: "Integration Rate",
      value: "89%",
      description: "Successful technical integrations",
      icon: Users,
      trend: "12 completed this quarter",
      color: "text-info"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Partnership Management</h1>
          <p className="text-muted-foreground mt-1">
            Build strategic partnerships and maximize mutual value creation opportunities
          </p>
        </div>
        <Button onClick={() => setShowProspectForm(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Partner Prospect
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {quickStats.map((stat, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
                </div>
                <div className={`p-2 rounded-lg bg-muted ${stat.color}`}>
                  <stat.icon className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground">{stat.trend}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search partners, prospects, or companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" className="flex items-center gap-2">
          <Target className="h-4 w-4" />
          Filters
        </Button>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="pipeline" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="tools">Dev Tools</TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="space-y-6">
          <PartnershipPipeline searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="active" className="space-y-6">
          <ActivePartnerships searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="categories" className="space-y-6">
          <PartnershipCategories searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <PartnershipAnalytics />
        </TabsContent>

        <TabsContent value="tools" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Partnership Development Tools</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Button variant="outline" className="h-20 flex flex-col gap-2">
                  <Search className="h-6 w-6" />
                  <span>Partner Research</span>
                </Button>
                <Button variant="outline" className="h-20 flex flex-col gap-2">
                  <Target className="h-6 w-6" />
                  <span>Fit Scoring</span>
                </Button>
                <Button variant="outline" className="h-20 flex flex-col gap-2">
                  <TrendingUp className="h-6 w-6" />
                  <span>Value Calculator</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Partner Prospect Form Modal */}
      {showProspectForm && (
        <PartnerProspectForm
          open={showProspectForm}
          onClose={() => setShowProspectForm(false)}
          onSuccess={() => {
            setShowProspectForm(false);
            // Refresh data
          }}
        />
      )}
    </div>
  );
};

export default PartnersPage;